import React, { useEffect, useState } from "react";
import SectionTitleAndDescription from "./SectionTitleAndDescription";
import Checkbox from "components/Common/Checkbox/Checkbox";
import SearchFilterDropdown, {
  OptionType,
} from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { SmallTitle } from "components/Common/Typography/Titles";
import { useDispatch, useSelector } from "react-redux";

import {
  addIndustryLeader,
  addIndustrysegment,
  addIndustryType,
  removeIndustryLeader,
  removeIndustryType,
} from "store/slices/searcSlice";
import { RootState } from "store/store";
import CustomTagWithPlus from "components/Common/Tag/CustomTagWithPlus";
import { useLazyQuery } from "@apollo/client";
import { COMPANY_TYPE } from "gql/boolean.gql";
import { readToken } from "services/localStorage.service";
import { getIndustryGrpLeaderResult, getIndustryGrpResult } from "api/search.api";
import { useDebounce } from "utils";
import Tag from "components/Common/Tag/Tag";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type CheckboxKey = "industry" | "industryLeader" | "segment";

const SelectIndustryComponent = ({ onChangeIndustry }: any) => {
  const [selectedCheckbox, setSelectedCheckbox] = useState<CheckboxKey | null>("industry");
  const [allIndustries, setAllIndustries] = useState<OptionType[]>([]);
  const [allIndustriesLeaders, setAllIndustriesLeaders] = useState<OptionType[]>([]);
  const [industriesLeaders, setIndustriesLeaders] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();

  const industries = useSelector((state: RootState) => state.search.industry_type);
  const industryLeaders = useSelector((state: RootState) => state.search.industry_leaders);
  const selectedSegments = useSelector((state: RootState) => state.search.segments);

  const handleCheckboxChange = (checkboxId: CheckboxKey) => {
    setSelectedCheckbox(checkboxId);
  };

  const placeholders: Record<CheckboxKey, string> = {
    industry: "Select an industry...",
    industryLeader: "Select an industry leader...",
    segment: "Select a segment...",
  };

  const updateSelectAllLabel = (isSelectAllSelected: boolean) => {
    const updatedMatches = [
      {
        value: isSelectAllSelected ? "unselect-all" : "select-all",
        label: isSelectAllSelected ? "Deselect All" : "Select All",
      },
      ...allIndustries.filter((item) => item.value !== "select-all" && item.value !== "unselect-all"),
    ];
    setAllIndustries(updatedMatches); // Update dropdown options dynamically
  };

  const updateSelectAllIndustriesLeadersLabel = (isSelectAllSelected: boolean) => {
    const updatedMatches = [
      {
        value: isSelectAllSelected ? "unselect-all" : "select-all",
        label: isSelectAllSelected ? "Deselect All" : "Select All",
      },
      ...allIndustriesLeaders.filter((item) => item.value !== "select-all" && item.value !== "unselect-all"),
    ];
    setAllIndustriesLeaders(updatedMatches);
  };

  const handleChangeIndustries = (selectedValues: any) => {
    onChangeIndustry();
    const lastSelectedValue = selectedValues[selectedValues.length - 1];
    const isSelectAllSelected = selectedValues.includes("select-all");
    const isSelectAllUnselected = selectedValues.includes("unselect-all");

    if (isSelectAllSelected) {
      // Select all industries
      const industriesToSelect = allIndustries
        .filter((industry) => industry.value !== "select-all")
        .map((industry) => industry.value);
      industriesToSelect.forEach((industry) => {
        dispatch(addIndustryType(industry));
      });
    } else if (isSelectAllUnselected) {
      // Unselect all industries
      const industriesToSelect = allIndustries
        .filter((industry) => industry.value !== "unselect-all")
        .map((industry) => industry.value);
      industriesToSelect.forEach((industry) => {
        dispatch(removeIndustryType(industry));
      });
    } else {
      // For normal selection/deselection
      if (selectedValues.includes(lastSelectedValue)) {
        dispatch(addIndustryType(lastSelectedValue));
      } else {
        dispatch(removeIndustryType(lastSelectedValue));
      }
    }
    // Update Select All / Unselect All label
    updateSelectAllLabel(isSelectAllSelected);
  };

  const handleChangeIndustriesLeaders = (selectedValues: any) => {
    const lastSelectedValue = selectedValues[selectedValues.length - 1];
    const isSelectAllSelected = selectedValues.includes("select-all");
    const isSelectAllUnselected = selectedValues.includes("unselect-all");

    // dispatch(addIndustryLeader(lastSelectedValue));
    if (isSelectAllSelected) {
      // Select all industries
      const industriesLeadersToSelect = allIndustriesLeaders
        .filter((industry) => industry.value !== "select-all")
        .map((industry) => industry.value);
      industriesLeadersToSelect.forEach((industry) => {
        dispatch(addIndustryLeader(industry));
      });
    } else if (isSelectAllUnselected) {
      // Unselect all industries
      const industriesLeadersToSelect = allIndustriesLeaders
        .filter((industry) => industry.value !== "unselect-all")
        .map((industry) => industry.value);
      industriesLeadersToSelect.forEach((industry) => {
        dispatch(removeIndustryLeader(industry));
      });
    } else {
      // For normal selection/deselection
      if (selectedValues.includes(lastSelectedValue)) {
        dispatch(addIndustryLeader(lastSelectedValue));
      } else {
        dispatch(removeIndustryLeader(lastSelectedValue));
      }
      if (selectedValues.includes(lastSelectedValue)) {
        industriesLeaders.filter((item) => {
          if (item.company === lastSelectedValue) {
            dispatch(addIndustryType(item.industry));
          }
        });
      }
    }
    // Update Select All / Unselect All label
    updateSelectAllIndustriesLeadersLabel(isSelectAllSelected);
  };

  const handleDeselect = (value: string | null) => {
    if (value) {
      if (selectedCheckbox === "industry") {
        onChangeIndustry && onChangeIndustry();
        dispatch(removeIndustryType(value));
      } else if (selectedCheckbox === "industryLeader") {
        dispatch(removeIndustryLeader(value));
        industriesLeaders.filter((item) => {
          if (item.company === value) {
            dispatch(removeIndustryType(item.industry));
          }
        });
      }
    }
  };

  const Titles: Record<"industry" | "industryLeader", string> = {
    industry: "Select Industry",
    industryLeader: "Select Industry Leader",
  };

  const [fetchCompanies, { loading, data, error }] = useLazyQuery(COMPANY_TYPE, {
    variables: { token: readToken() },
  });

  const companiesType = data?.companyGroups?.result || [];

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Get default values based on selectedCheckbox
  const defaultValues = {
    industry: industries,
    industryLeader: industryLeaders,
    // segment: segments,
  };

  const fetchIndustries = async (query: string) => {
    setIsLoading(true);

    try {
      const response = await getIndustryGrpResult(query);
      const data: any = response;
      const matches = data?.data?.autocompleteCompanyIndustry?.matches
        .sort((a: any, b: any) => (a.industry || "").localeCompare(b.industry || ""));
      console.log("Matches : ", matches);
      // setAllIndustries(
      //   matches?.map((item: any) => ({
      //     value: item.industry,
      //     label: item.industry,
      //   }))
      // );
      if (matches.length > 1) {
        const updatedMatches = [
          { value: "select-all", label: "Select All" },
          ...matches.map((item: any) => ({
            value: item.industry,
            label: item.industry,
          })),
        ];
        setAllIndustries(updatedMatches);
      } else {
        setAllIndustries(
          matches?.map((item: any) => ({
            value: item.industry,
            label: item.industry,
          }))
        );
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchIndustryLeaders = async (query: string) => {
    setIsLoading(true);

    try {
      const response = await getIndustryGrpLeaderResult(query);
      const data: any = response;
      const matches = data?.data?.autocompleteCompanyIndustry?.matches;
      setIndustriesLeaders(matches);
      if (matches.length > 1) {
        const updatedMatches = [
          { value: "select-all", label: "Select All" },
          ...matches.map((item: any) => ({
            value: item.company,
            label: item.company,
          })),
        ];
        setAllIndustriesLeaders(updatedMatches);
      } else {
        setAllIndustriesLeaders(
          matches?.map((item: any) => ({
            value: item.company,
            label: item.company,
          }))
        );
      }
      // setAllIndustries(
      //   matches?.map((item: any) => ({
      //     value: item.company,
      //     label: item.company,
      //   }))
      // );
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearchSkill = useDebounce((query: string) => {
    if (query) {
      if (selectedCheckbox === "industry") {
        fetchIndustries(query);
      } else if (selectedCheckbox === "industryLeader") {
        fetchIndustryLeaders(query);
      }
    }
  }, 500);

  const handleInputChange = (inputValue: string) => {
    debouncedSearchSkill(inputValue);
  };

  const addSegment = (value: string) => {
    dispatch(addIndustrysegment(value));
  };

  const handleSegmentSelectAll = () => {
    companiesType.map((item: string) => {
      if (!selectedSegments.includes(item)) {
        addSegment(item);
      }
    });
  };

  const handleSegmentDeSelectAll = () => {
    companiesType.map((item: string) => {
      addSegment(item);
    });
  };

  return (
    <>
      <div className="w-1/4">
        <SectionTitleAndDescription
          title="Select Industry"
          isRequiredSection={false}
          isHelpInfoReq={false}
          description="Select relevant industries or segments."
        />
      </div>

      <div className="w-3/4">
        <div className="xl:flex grid gap-4 items-center mb-4">
          <Checkbox
            inputId="selectIndustry"
            inputName="industry"
            inputValue=""
            labelFor="selectIndustry"
            labelTitle="Industry"
            checked={selectedCheckbox === "industry"}
            onChange={() => handleCheckboxChange("industry")}
          />
          <Checkbox
            inputId="selectIndustryLeader"
            inputName="industryLeader"
            inputValue=""
            labelFor="selectIndustryLeader"
            labelTitle="Industry Leader"
            checked={selectedCheckbox === "industryLeader"}
            onChange={() => handleCheckboxChange("industryLeader")}
          />
          <Checkbox
            inputId="selectSegment"
            inputName="segment"
            inputValue=""
            labelFor="selectSegment"
            labelTitle="Segment"
            checked={selectedCheckbox === "segment"}
            onChange={() => handleCheckboxChange("segment")}
          />
        </div>
        {selectedCheckbox && (
          <div>
            {selectedCheckbox !== "segment" ? (
              <>
                <SmallTitle
                  text={Titles[selectedCheckbox]}
                  font="font-medium"
                  className="text-secondary mb-4"
                />
                <div className="flex flex-col border-2 rounded-lg border-main-border border-dashed p-7">
                  <div className="xl:flex grid xl:gap-12 gap-5 pb-4 items-center">
                    <div className="w-full">
                      {/* <Label labelTitle={title} isHelpIconRequired tooltipText={"This is a tooltip"} /> */}
                      <div className="SelectProject-input">
                        <SearchFilterDropdown
                          title="hidden"
                          mode="multiple"
                          defaultValue={defaultValues[selectedCheckbox]}
                          options={selectedCheckbox === "industry" ? allIndustries : allIndustriesLeaders}
                          placeholder={placeholders[selectedCheckbox]}
                          maxSelect={100}
                          // onChange={handleChange}

                          onChange={selectedCheckbox === "industry" ? handleChangeIndustries : handleChangeIndustriesLeaders}
                          showTooltip={true}
                          isRightIcon={selectedCheckbox === "industry" ? false : true}
                          handleDeselect={handleDeselect}
                          onInputChange={handleInputChange}
                          tooltipText={
                            selectedCheckbox === "industry" ? "Select industry" : "Select Company"
                          }
                          loading={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                {companiesType?.map((item: string) => (
                  <React.Fragment key={item}>
                    <CustomTagWithPlus
                      value={item}
                      isSelected={selectedSegments.includes(item)}
                      handleClick={addSegment}
                    />
                  </React.Fragment>
                ))}
                {
                  selectedSegments.length == companiesType.length ?
                    (
                      <button type="button" onClick={() => handleSegmentDeSelectAll()} style={{ color: 'red', fontWeight: 400 }} className="border-dotted border-2 border-danger-border pt-2 pb-2 pl-5 pr-5 rounded-md text-sm flex items-center gap-1 font-medium"><span>Deselect All</span></button>
                    ) : (
                      <button type="button" onClick={() => handleSegmentSelectAll()} className="border-dotted border-2 border-primary-border pt-2 pb-2 pl-5 pr-5 text-primary rounded-md text-sm flex items-center gap-1 font-medium"><span>Select All</span></button>
                    )
                }
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SelectIndustryComponent;
